import { AlbumVariationView } from '@zola/svc-web-api-ts-client';
import Icon8x8 from '../../../../../public/static-preauth/images/WeddingAlbums/pricingMatrix/8x8.svg';
import Icon10x8 from '../../../../../public/static-preauth/images/WeddingAlbums/pricingMatrix/10x8.svg';
import Icon10x10 from '../../../../../public/static-preauth/images/WeddingAlbums/pricingMatrix/10x10.svg';
import Icon12x12 from '../../../../../public/static-preauth/images/WeddingAlbums/pricingMatrix/12x12.svg';
import Icon14x11 from '../../../../../public/static-preauth/images/WeddingAlbums/pricingMatrix/14x11.svg';

import {
  AlbumIcon,
  BorderedRow,
  CellInfo,
  CellSubtitle,
  CellTitle,
  CountSubtitle,
  desktopOnly,
  Disclaimer,
  highlightBackground,
  IconWrapper,
  Matrix,
  MatrixColgroup,
  MatrixColumn,
  MatrixTable,
  MatrixTableCell,
  MatrixTableRow,
  MatrixTableTitleCell,
  MatrixTitle,
  PageCount,
  PageCountCell,
  PopularCell,
  PopularCellTitle,
  ShorterCell,
  Subheader,
  TableWrapper,
} from './PricingMatrix.styles';

type AlbumReactElements = {
  colgroups: JSX.Element[];
  spacerRow: JSX.Element[];
  headerRow: JSX.Element[];
};

const PricingMatrix = ({
  matrixData,
}: {
  matrixData: Record<string, Array<AlbumVariationView>>;
}): JSX.Element => {
  const albums = [
    {
      additionalPagePrice: (matrixData['8x8'][0].additional_page_price_cents || 0) / 100,
      basePrice: (matrixData['8x8'][0].price_cents || 0) / 100,
      icon: {
        src: Icon8x8,
        alt: 'Photo representation of 8" x 8" album',
        width: '54',
        height: '54',
      },
      shape: 'Square',
      size: '8" x 8"',
    },
    {
      additionalPagePrice: (matrixData['10x8'][0].additional_page_price_cents || 0) / 100,
      basePrice: (matrixData['10x8'][0].price_cents || 0) / 100,
      icon: {
        src: Icon10x8,
        alt: 'Photo representation of 10" x 8" album',
        width: '68',
        height: '54',
      },
      shape: 'Landscape',
      size: '10" x 8"',
    },
    {
      additionalPagePrice: (matrixData['10x10'][0].additional_page_price_cents || 0) / 100,
      basePrice: (matrixData['10x10'][0].price_cents || 0) / 100,
      icon: {
        src: Icon10x10,
        alt: 'Photo representation of 10" x 10" album',
        width: '68',
        height: '68',
      },
      shape: 'Square',
      size: '10" x 10"',
      mostPopular: true,
    },
    {
      additionalPagePrice: (matrixData['12x12'][0].additional_page_price_cents || 0) / 100,
      basePrice: (matrixData['12x12'][0].price_cents || 0) / 100,
      icon: {
        src: Icon12x12,
        alt: 'Photo representation of 12" x 12" album',
        width: '81',
        height: '81',
      },
      shape: 'Square',
      size: '12" x 12"',
    },
    {
      additionalPagePrice: (matrixData['14x11'][0].additional_page_price_cents || 0) / 100,
      basePrice: (matrixData['14x11'][0].price_cents || 0) / 100,
      icon: {
        src: Icon14x11,
        alt: 'Photo representation of 14" x 11" album',
        width: '95',
        height: '75',
      },
      shape: 'Landscape',
      size: '14" x 11"',
    },
  ];

  const pageCounts = [20, 30, 40, 50, 60, 70, 80];
  const averagePageLengthIndex = 2;

  const initialAlbumElements: AlbumReactElements = {
    colgroups: [],
    spacerRow: [],
    headerRow: [],
  };

  const albumElements = albums.reduce((accumulator, currentAlbum, index) => {
    const { mostPopular, size, icon, shape } = currentAlbum;

    accumulator.colgroups.push(
      <MatrixColumn key={`column-${index}`} css={mostPopular ? highlightBackground : null} />
    );
    accumulator.spacerRow.push(
      mostPopular ? (
        <PopularCell key={`spacer-${index}`}>
          <PopularCellTitle>Most popular size</PopularCellTitle>
        </PopularCell>
      ) : (
        <ShorterCell key={`spacer-${index}`} />
      )
    );
    accumulator.headerRow.push(
      <MatrixTableTitleCell key={size}>
        <IconWrapper>
          <AlbumIcon src={icon.src} alt={icon.alt} width={icon.width} height={icon.height} />
        </IconWrapper>
        <CellTitle>{size}</CellTitle>
        <CellSubtitle>{shape}</CellSubtitle>
      </MatrixTableTitleCell>
    );

    return accumulator;
  }, initialAlbumElements);

  const startingAtLabels = Array(albums.length).fill(
    <ShorterCell>
      <CellSubtitle>Starting at:</CellSubtitle>
    </ShorterCell>
  );

  return (
    <Matrix>
      <MatrixTitle>Album pricing breakdown*</MatrixTitle>
      <Subheader>On average, Zola couples use 30 to 40 pages in their albums.</Subheader>
      <TableWrapper>
        <MatrixTable>
          <MatrixColgroup>
            <MatrixColumn />
            {albumElements.colgroups}
          </MatrixColgroup>
          <MatrixTableRow>
            <ShorterCell />
            {albumElements.spacerRow}
          </MatrixTableRow>
          <MatrixTableRow>
            <MatrixTableCell />
            {albumElements.headerRow}
          </MatrixTableRow>
          <BorderedRow>
            <ShorterCell />
            {startingAtLabels}
          </BorderedRow>
          {pageCounts.map((count, index) => {
            const additionalPages = count - pageCounts[0];
            const averageLength = index === averagePageLengthIndex;
            return (
              <BorderedRow
                key={`price-row-${index}`}
                css={averageLength ? highlightBackground : null}
              >
                <PageCountCell isTaller={averageLength}>
                  <PageCount isHighlightedCell={averageLength}>{count} pages</PageCount>
                  {averageLength && (
                    <CountSubtitle>
                      Average <span css={desktopOnly}>album </span>length
                    </CountSubtitle>
                  )}
                </PageCountCell>
                {albums.map(currentAlbum => {
                  const { basePrice, additionalPagePrice, mostPopular } = currentAlbum;
                  const price = basePrice + additionalPagePrice * additionalPages;
                  return (
                    <MatrixTableCell
                      isDoubleHighlighted={averageLength && mostPopular}
                      isTaller={averageLength}
                    >
                      <CellInfo isHighlightedCell={averageLength}>${price}</CellInfo>
                    </MatrixTableCell>
                  );
                })}
              </BorderedRow>
            );
          })}
        </MatrixTable>
      </TableWrapper>
      <Disclaimer>
        *Numbers are based on the starting price of a Classic Album. Premier and Deluxe albums are
        limited to 80 pages due to paper thickness.
      </Disclaimer>
    </Matrix>
  );
};

export default PricingMatrix;
